import React, { useContext } from 'react'
import { AggregateDocsResponse, AggregateDocsQuerySettings } from '../api/Types'
import * as Service from '../api/Service'

interface Props {
  readonly querySettings?: AggregateDocsQuerySettings
}

interface State {
  readonly docs: AggregateDocsResponse
  readonly isLoading: boolean
  readonly querySettings: AggregateDocsQuerySettings
}

interface DataContextProps {
  readonly isLoading: boolean
  readonly docs: AggregateDocsResponse
  readonly querySettings: Partial<AggregateDocsQuerySettings>
}

interface ProviderContextProps {
  readonly fetchDocs: (queryParams: AggregateDocsQuerySettings) => void
  readonly updateQuery: (queryParams: AggregateDocsQuerySettings) => void
}

type DocsContextProps = DataContextProps & Partial<ProviderContextProps>

const initialDocsState: AggregateDocsResponse = {
  faqs: [],
  tech_docs: [],
  webinars: [],
  tutorial_videos: [],
  case_studies: [],
}

export const DocsContext = React.createContext<DocsContextProps>({
  docs: initialDocsState,
  isLoading: false,
  querySettings: {},
})

export const DocsConsumer = DocsContext.Consumer

class DocsProvider extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      querySettings: {
        ...this.props.querySettings,
      },
      docs: initialDocsState,
      isLoading: true,
    }
    this.fetchDocs = this.fetchDocs.bind(this)
    this.updateQuery = this.updateQuery.bind(this)
  }

  async componentDidMount() {
    this.fetchDocs()
  }

  async fetchDocs() {
    const { querySettings } = this.state
    this.setState({ isLoading: true })
    try {
      const response = await Service.FetchAggregateDocs(querySettings)
      this.setState({ docs: response, isLoading: false })
    } catch (error) {
      toastr.error('Failed to fetch resources from the server.')
      this.setState({ isLoading: false })
    }
  }

  async updateQuery(newValues: Partial<AggregateDocsQuerySettings>) {
    this.setState(
      {
        querySettings: {
          ...this.state.querySettings,
          ...newValues,
        },
      },
      this.fetchDocs
    )
  }

  render() {
    const { docs, isLoading, querySettings } = this.state
    return (
      <DocsContext.Provider
        value={{
          docs,
          isLoading,
          querySettings,
          fetchDocs: this.fetchDocs,
          updateQuery: this.updateQuery,
        }}
      >
        {this.props.children}
      </DocsContext.Provider>
    )
  }
}

export const useDocsContext = (): DocsContextProps => {
  const context = useContext(DocsContext)
  if (!context) {
    throw new Error('useDocsContext must be used within a DocsProvider')
  }

  return context
}

export default DocsProvider
